// frontend/src/components/SearchableSelect.js
import React, { useState, useRef, useEffect } from 'react';
import { colors, radius, shadows } from '../styles/theme';
import { searchMatch } from '../utils/searchMatch';

// Aranabilir select. İç arama LOCAL state'tir (URL'e yazılmaz).
// compact: dar satır/tablo bağlamı için küçük padding/font. minWidth: sarmalayıcı genişlik tabanı.
// maxVisible: uzun listede gösterilen sonuç sayısını sınırla (ilk N + "daraltmak için yazın" ipucu).
// option.searchText: arama bu alanla eşleşir (yoksa label) — gizli kod vb. ile arama için.
const SearchableSelect = ({ options, value, onChange, placeholder = 'Ara…', disabled, invalid, compact, minWidth, maxVisible }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef(null);
  const selected = options.find(o => String(o.value) === String(value));
  const matched = search ? options.filter(o => searchMatch(o.searchText || o.label, search)) : options;
  const filtered = maxVisible ? matched.slice(0, maxVisible) : matched;
  const truncated = maxVisible && matched.length > maxVisible;

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: 'relative', minWidth }}>
      <input
        type="text"
        disabled={disabled}
        placeholder={placeholder}
        value={open ? search : (selected ? selected.label : '')}
        onChange={e => { setSearch(e.target.value); setOpen(true); setActiveIndex(-1); }}
        onFocus={() => { setOpen(true); setSearch(''); setActiveIndex(-1); }}
        onKeyDown={(e) => {
          if (!open) return;                 // kapalıyken Enter'a karışma → EnterNavForm advance serbest
          if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, filtered.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); }
          else if (e.key === 'Enter') {
            e.preventDefault();              // açıkken Enter advance YAPMASIN
            const opt = filtered[activeIndex >= 0 ? activeIndex : 0];
            if (opt) { onChange(opt.value); setOpen(false); setSearch(''); setActiveIndex(-1); }
          } else if (e.key === 'Escape') { setOpen(false); }
        }}
        style={{
          width: '100%', padding: compact ? '4px 8px' : '8px 12px', boxSizing: 'border-box',
          fontSize: compact ? '13px' : '14px',
          border: `${invalid ? 2 : 1}px solid ${invalid ? colors.danger : colors.border}`,
          borderRadius: radius.sm,
        }}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
          maxHeight: '220px', overflowY: 'auto', background: colors.surface,
          border: `1px solid ${colors.border}`, borderRadius: '0 0 6px 6px', boxShadow: shadows.md,
        }}>
          {value && (
            <div onMouseDown={() => { onChange(''); setOpen(false); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', color: colors.textTertiary, borderBottom: `1px solid ${colors.borderLight}` }}>
              Seçimi temizle
            </div>
          )}
          {truncated && (
            <div style={{ padding: '6px 12px', fontSize: '11px', color: colors.textTertiary, borderBottom: `1px solid ${colors.borderLight}`, background: colors.backgroundAlt }}>
              İlk {maxVisible} sonuç — daraltmak için yazın
            </div>
          )}
          {filtered.length === 0 ? (
            <div style={{ padding: '8px 12px', fontSize: '13px', color: colors.textTertiary }}>Sonuç yok</div>
          ) : filtered.map((o, i) => (
            <div key={o.value} onMouseDown={() => { onChange(o.value); setOpen(false); setSearch(''); setActiveIndex(-1); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: `1px solid ${colors.borderLight}`, background: i === activeIndex ? colors.backgroundAlt : 'transparent' }}
              onMouseEnter={e => { e.currentTarget.style.background = colors.backgroundAlt; }}
              onMouseLeave={e => { e.currentTarget.style.background = i === activeIndex ? colors.backgroundAlt : 'transparent'; }}>
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
